import {Offcanvas} from 'bootstrap';

// Anchor navigation (partials/header/navi.onepage.html.twig) and section rail (partials/page/section-rail.html.twig): every
// link that jumps to a section of this page. A section can have a link in both; they are marked together.
// - scroll spy: the links of the section in view get aria-current="location" and .active (in the navigation also the list
//   item, the indicator of initNaviGlide.js follows .nav-item.active, told by the event navactivechange on the list).
//   Active is the last section whose top has passed a line at 30 % of the screen (below the bar: scroll-padding-top of
//   html, base/_base.scss), so a section reached by a jump is active at once. One IntersectionObserver watches the band
//   between the bar and that line: a section crossing the line enters or leaves it. At the end of the page the last
//   visible entry wins, above the first section none.
// - click (one listener per link group): the mobile menu (offcanvas #navBar) closes first, then the page jumps, so the
//   scroll lock of the menu cannot swallow the jump; the focus moves to the section. aria-expanded of the menu button:
//   initMobileNav.js.
// Without such links nothing starts. Returns a function that removes observers and listeners again.
const GROUPS = [
    ['#navMain', 'a.nav-link[href*="#"]'],
    ['.section-rail', 'a[href*="#"]'],
];

export default function initAnchorNav() {
    const list = document.getElementById('navMain');
    const containers = GROUPS.map(([selector, links]) => [document.querySelector(selector), links]).filter(([container]) => container);

    // one entry per section, its links from all groups, in the order of the page
    const bySection = new Map();
    containers.forEach(([container, selector]) => container.querySelectorAll(selector).forEach((link) => {
        const section = sectionOf(link);
        if (section) {
            bySection.set(section, [...(bySection.get(section) ?? []), link]);
        }
    }));
    const entries = [...bySection].map(([section, links]) => ({section, links}))
        .sort((a, b) => (a.section.compareDocumentPosition(b.section) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));

    if (!entries.length) {
        return () => {};
    }

    const menu = document.getElementById('navBar');
    const listeners = new AbortController();
    let atEnd = false;
    let current = null;
    let spy = null;

    const mark = (entry) => {
        if (entry === current) {
            return;
        }
        current = entry;
        entries.forEach(({links}) => links.forEach((link) => {
            const on = entry !== null && entry.links.includes(link);
            link.classList.toggle('active', on);
            if (link.parentElement.classList.contains('nav-item')) {
                link.parentElement.classList.toggle('active', on);
            }
            if (on) {
                link.setAttribute('aria-current', 'location');
            } else {
                link.removeAttribute('aria-current');
            }
        }));
        list?.dispatchEvent(new CustomEvent('navactivechange'));
    };

    const update = () => {
        if (atEnd) {
            const visible = entries.filter(({section}) => section.getBoundingClientRect().top < window.innerHeight);
            mark(visible[visible.length - 1] ?? null);
            return;
        }
        const line = activationLine();
        const passed = entries.filter(({section}) => section.getBoundingClientRect().top <= line);
        mark(passed[passed.length - 1] ?? null);
    };

    const observe = () => {
        spy?.disconnect();
        spy = new IntersectionObserver(update, {
            rootMargin: `-${barHeight()}px 0px -${Math.round(window.innerHeight - activationLine())}px 0px`,
        });
        entries.forEach(({section}) => spy.observe(section));
    };

    // the end of the page (a short last section never reaches the band): a point at the bottom of the body (position-relative)
    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;bottom:0;left:0;width:1px;height:1px;pointer-events:none';
    document.body.append(sentinel);
    const end = new IntersectionObserver(([change]) => {
        atEnd = change.isIntersecting;
        update();
    });
    end.observe(sentinel);

    observe();

    // the bar height changes with the breakpoint (md): build the band again once resizing has settled
    let resizing = 0;
    window.addEventListener('resize', () => {
        clearTimeout(resizing);
        resizing = setTimeout(observe, 200);
    }, {signal: listeners.signal});

    containers.forEach(([container]) => container.addEventListener('click', (event) => {
        const link = event.target.closest('a');
        const entry = link && entries.find(({links}) => links.includes(link));
        if (!entry || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }
        event.preventDefault();

        const menuOpen = menu?.classList.contains('show');
        if (menuOpen) {
            menu.addEventListener('hidden.bs.offcanvas', () => jumpTo(entry.section, link.hash), {once: true, signal: listeners.signal});
            Offcanvas.getOrCreateInstance(menu).hide();
        } else {
            jumpTo(entry.section, link.hash);
        }
    }, {signal: listeners.signal}));

    return () => {
        listeners.abort();
        clearTimeout(resizing);
        spy?.disconnect();
        end.disconnect();
        sentinel.remove();
        mark(null);
    };
}

// the section a link points to, only on this page (on other pages the link of the anchor navigation leads to the page first)
function sectionOf(link) {
    const url = new URL(link.href, window.location.href);
    if (!url.hash || url.origin !== window.location.origin || url.pathname !== window.location.pathname) {
        return null;
    }

    return document.getElementById(decodeURIComponent(url.hash.slice(1)));
}

// a section is active once its top is above this line: 30 % of the screen, at least just below the bar
function activationLine() {
    return Math.max(window.innerHeight * 0.3, barHeight() + 2);
}

// the height that jump targets clear (scroll-padding-top of html, base/_base.scss)
function barHeight() {
    return Math.round(parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0);
}

// scroll as the page does (smooth unless the visitor asked for reduced motion), keep the address and move the focus there
function jumpTo(section, hash) {
    if (window.location.hash !== hash) {
        history.pushState(null, '', hash);
    }
    section.scrollIntoView({block: 'start'});
    if (!section.hasAttribute('tabindex')) {
        section.setAttribute('tabindex', '-1');
    }
    section.focus({preventScroll: true});
}
